/** Glisser une unité depuis une liste vers la carte (docs/14 §2.6, étape 3) : pont entre la carte, qui sait
 *  convertir un point de la fenêtre en coordonnées et connaît l'emprise admise du dossier, et les listes d'unités
 *  (vue Manœuvre, ordre de bataille), qui suivent le pointeur. Événements pointeur (souris, doigt, stylet), sans
 *  glisser-déposer HTML5, peu fiable au doigt. */

/** Ce que la carte expose aux listes pendant un glissement. */
export interface MapDropTarget {
  /** Point de la fenêtre (clientX, clientY) → [lon, lat] s'il tombe sur la carte visible (ni hors de la carte, ni
   *  sous un panneau, une barre ou une fiche) ; null sinon. */
  locate: (x: number, y: number) => [number, number] | null;
  /** Point dans l'emprise admise du dossier (celle que le serveur contrôle). */
  inside: (at: [number, number]) => boolean;
  /** Centre de la carte affichée (pose au clavier, sans glisser). */
  center: () => [number, number] | null;
  /** Cadre la carte sur le camp ou la zone (action proposée après un dépôt refusé). */
  fit: () => void;
  /** Zone libre (textes « zone ») plutôt que camp. */
  freeZone: () => boolean;
}

let target: MapDropTarget | null = null;

/** Branche la carte (au chargement du moteur) ; rend de quoi la débrancher. */
export function registerMapDrop(next: MapDropTarget): () => void {
  target = next;
  return () => {
    if (target === next) target = null;
  };
}

export function mapDropTarget(): MapDropTarget | null {
  return target;
}

/** Seuil d'activation du glissement : 4 px à la souris, 10 px au doigt (comme les gestes de la carte). */
export const LIST_DRAG_SLOP_PX = { mouse: 4, touch: 10 } as const;

export function dragActivated(start: { x: number; y: number }, now: { x: number; y: number }, touch: boolean): boolean {
  return Math.hypot(now.x - start.x, now.y - start.y) >= (touch ? LIST_DRAG_SLOP_PX.touch : LIST_DRAG_SLOP_PX.mouse);
}

/** Issue d'un dépôt : unité posée en `at`, ou refus (hors de la carte, hors de l'emprise du dossier). */
export type DropOutcome =
  | { kind: "place"; at: [number, number] }
  | { kind: "outside-map" }
  | { kind: "outside-zone"; at: [number, number] };

export function dropOutcome(map: MapDropTarget | null, x: number, y: number): DropOutcome {
  return outcomeAt(map, map?.locate(x, y) ?? null);
}

/** Issue d'une pose au centre de la carte (menu de l'unité, au clavier). */
export function centerOutcome(map: MapDropTarget | null): DropOutcome {
  return outcomeAt(map, map?.center() ?? null);
}

function outcomeAt(map: MapDropTarget | null, at: [number, number] | null): DropOutcome {
  if (!map || !at) return { kind: "outside-map" };
  return map.inside(at) ? { kind: "place", at } : { kind: "outside-zone", at };
}
